import type { User } from 'payments-api/model';
import clsx from 'clsx';
import { InvoiceLineItemType } from 'payments-api/model/invoiceLineItemType';
import React, { useMemo } from 'react';
import { makePath } from 'utils/routes';
import { useLocation } from 'wouter';

import type { CurrencyCode } from '@fragment/types/currencyCodes';
import { currencyPrecision as currencyPrecisionMap } from '@fragment/types/currencyCodes';
import { kLabelColumnWidth } from '@fragment/ui/src/components/Payments/LayoutRow';

import { FormatBillingAmount } from './formatBillingAmount';
import { PaymentsModal } from './PaymentsModal';
import { RoleColorDot } from './RoleColorDot';

const LabelRow = ({
  label,
  children,
  last,
}: {
  label: string;
  children: React.ReactNode;
  last?: boolean;
}) => (
  <div
    className={clsx(
      'flex items-center',
      !last && 'border-b-[0.5px] border-main-200'
    )}
  >
    <div
      className={clsx(
        'px-f1 py-f1 text-main-500 border-r-[0.5px] border-main-200 shrink-0',
        kLabelColumnWidth
      )}
    >
      {label}
    </div>
    <div className="px-f1 py-f1 text-main flex-1">{children}</div>
  </div>
);

type Props = {
  user: User;
  invoiceLineItems: { type: string; amount: string; currencyCode: string }[];
  workspaceId: string;
  onClose: () => void;
};

export const UserDetailModal = ({
  user,
  invoiceLineItems,
  workspaceId,
  onClose,
}: Props): JSX.Element => {
  const [, setLocation] = useLocation();

  const { totalPayins, totalPayouts } = useMemo(() => {
    let payins = BigInt(0);
    let payouts = BigInt(0);
    invoiceLineItems.forEach((li) => {
      if (li.type === InvoiceLineItemType.payin) {
        payins += BigInt(li.amount);
      } else {
        payouts += BigInt(li.amount);
      }
    });
    return { totalPayins: payins, totalPayouts: payouts };
  }, [invoiceLineItems]);

  const handleFullProfile = () => {
    onClose();
    setLocation(
      makePath(['w', workspaceId, 'payments', 'users', user.external_id], {
        absolute: true,
      })
    );
  };

  return (
    <PaymentsModal
      data-testid="user-detail-modal"
      onClose={onClose}
      width="sm"
      body={
        <div className="flex flex-col w-full text-xmd font-helvetica">
          {/* Content with border */}
          <div className="border-[0.5px] border-main-200">
            {/* Header — 3 rows high */}
            <div className="flex items-start p-f1 h-f12 border-b-[0.5px] border-main-200">
              <span className="flex items-center gap-x-f1">
                <RoleColorDot role={user.role} size={20} />
                <span className="text-lg text-main">{user.external_id}</span>
              </span>
            </div>

            <LabelRow label="Role">{user.role}</LabelRow>
            <LabelRow label="Total Payins">
              <FormatBillingAmount
                amount={Number(totalPayins)}
                currencyCode={'USD' as CurrencyCode}
                currencyPrecision={currencyPrecisionMap.USD}
              />
            </LabelRow>
            <LabelRow label="Total Payouts" last>
              <FormatBillingAmount
                amount={Number(totalPayouts)}
                currencyCode={'USD' as CurrencyCode}
                currencyPrecision={currencyPrecisionMap.USD}
              />
            </LabelRow>
          </div>

          {/* Full Profile button */}
          <div className="pt-f2">
            <button
              type="button"
              onClick={handleFullProfile}
              className={clsx(
                'flex items-center justify-center gap-x-[0.5ch]',
                'p-[4px] px-[9px]',
                'font-helvetica text-xmd rounded-[2px]',
                'bg-main-200 text-main hover:bg-main-300 cursor-pointer',
                'transition-colors'
              )}
            >
              <span>Full Profile</span>
              <span className="font-FragmentMono">→</span>
            </button>
          </div>
        </div>
      }
    />
  );
};
